import { CreateTemplateBlock } from '@/components/chat/components/CreateTemplateBlock';
import { useBlockErrorReporter } from '@/hooks/useEvidenceAnalytics';
import type { ToolUseItem } from '@heidi/agents/contracts';

import {
  readString,
  readToolData,
  readToolError,
  toCreateTemplateStatus,
} from '@heidi/agents';
import { friendlyToolError } from '@heidi/agents/data';

// eslint-disable-next-line react-refresh/only-export-components -- Adapter exports must keep their existing render function names.
const CreateTemplateToolBlock = (
  props: Omit<Parameters<typeof CreateTemplateBlock>[0], 'onBlockError'>
): JSX.Element => {
  const reportBlockError = useBlockErrorReporter();
  return <CreateTemplateBlock {...props} onBlockError={reportBlockError} />;
};

export const renderCreateTemplateToolUse = (item: ToolUseItem): JSX.Element | null => {
  // A queued create can still be the first event of a multi-template approval
  // batch. Let the timeline's quiet Working state own this ambiguous phase;
  // rendering a singular card here causes a visible one-template → batch swap.
  if (item.status === 'queued') return null;
  const status = toCreateTemplateStatus(item.status);
  if (!status) return null;

  const data = readToolData(item);
  const templateId =
    readString(data, 'template_id') ?? readString(item.input, 'template_id');
  // create_template's result names the template field `name` (not template_name).
  const templateName = readString(data, 'name') ?? readString(item.input, 'name');

  // Without anything to identify the template the rich block carries no more
  // information than GenericToolUseRow — fall through cleanly.
  if (!templateId && !templateName) return null;

  if (status === 'error') {
    return (
      <CreateTemplateToolBlock
        status="error"
        templateId={templateId}
        templateName={templateName}
        error={friendlyToolError(readToolError(item))}
      />
    );
  }

  return (
    <CreateTemplateToolBlock
      status={status}
      templateId={templateId}
      templateName={templateName}
    />
  );
};
